import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import { InMemoryCache } from "apollo-cache-inmemory";
import { ApolloClient } from "apollo-client";
import { split } from "apollo-link";
import { setContext } from "apollo-link-context";
import { HttpLink } from "apollo-link-http";
import { WebSocketLink } from "apollo-link-ws";
import { getMainDefinition } from "apollo-utilities";
import { createNetworkStatusNotifier } from "react-apollo-network-status";
import { SubscriptionClient } from "subscriptions-transport-ws";

import { SessionHelper } from "../utils";

// You should use an absolute URL here
const options = {
  httpUri: process.env.REACT_APP_GRAPHQL_API || "http://localhost:4000/graphql",
  wsUri: process.env.REACT_APP_GRAPHQL_WS_API || "ws://localhost:4000/graphql",
};
console.log(options);

const { link, useApolloNetworkStatus } = createNetworkStatusNotifier();
const cache = new InMemoryCache();

const MAX_RETRIES = 3;

const retryLink = new RetryLink({
  attempts: {
    max: MAX_RETRIES,
    retryIf: (error, _operation) => {
      if (!error) return false;
      // HTTP error from Traefik (502, 503, 504 during scale-down)
      if (error.statusCode >= 500) return true;
      // Pure network failure (connection refused, DNS, timeout) — no response
      return !error.result;
    },
  },
  delay: {
    initial: 300,
    max: 5000,
    jitter: true,
  },
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.forEach(({ message, locations, path }) =>
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
    );
  }
  if (networkError) console.log(`[Network error]: ${networkError}`);
});

let httpLink = new HttpLink({
  uri: options.httpUri,
});

// Create the subscription websocket link if available
if (options.wsUri) {
  const token = SessionHelper.token;
  const subscriptionClient = new SubscriptionClient(options.wsUri, {
    reconnect: true,
    connectionParams: () => ({
      authToken: token,
    }),
  });

  const wsLink = new WebSocketLink(subscriptionClient);

  // using the ability to split links, you can send data to each link
  // depending on what kind of operation is being sent
  httpLink = split(
    // split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink,
    httpLink
  );
}

const authLink = setContext((_, { headers }) => {
  const token = SessionHelper.token;
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      Authorization: `Bearer ${token}`,
    },
  };
});

export { useApolloNetworkStatus, cache };

// Create the apollo client instance
export default new ApolloClient({
  link: link.concat(errorLink.concat(retryLink.concat(authLink.concat(httpLink)))),
  cache,
  connectToDevTools: true,
});
